import { ColorResult, HSLColor } from "react-color";

interface ParsedRgbaColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

interface RgbColor {
  r: number;
  g: number;
  b: number;
}

export interface RgbaColor extends RgbColor {
  a: number;
}

export function parseRgbaString(colorString: string): ParsedRgbaColor | null {
  if (!colorString) {
    return null;
  }
  const lowerColorString = colorString.toLowerCase().trim();
  let match = lowerColorString.match(
    /^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)$/
  );
  if (match) {
    return {
      r: parseInt(match[1], 10),
      g: parseInt(match[2], 10),
      b: parseInt(match[3], 10),
      a: parseFloat(match[4]),
    };
  }
  match = lowerColorString.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
  if (match) {
    return {
      r: parseInt(match[1], 10),
      g: parseInt(match[2], 10),
      b: parseInt(match[3], 10),
      a: 1,
    };
  }
  return null;
}

function componentToHex(c: number): string {
  const clamped = Math.max(0, Math.min(255, Math.round(c)));
  const hex = clamped.toString(16);
  return hex.length === 1 ? "0" + hex : hex;
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(
    b
  )}`.toUpperCase();
}

export function formatColorDisplayPart(
  colorPropValue: string,
  displayType: "color" | "opacity"
): string | number {
  if (!colorPropValue || colorPropValue.toLowerCase() === "transparent") {
    return displayType === "color" ? "Transparent" : 0;
  }
  if (colorPropValue.toLowerCase() === "none") {
    return displayType === "color" ? "Ingen" : 100;
  }

  const parsedRgba = parseRgbaString(colorPropValue);

  if (displayType === "color") {
    if (parsedRgba) {
      return rgbToHex(parsedRgba.r, parsedRgba.g, parsedRgba.b);
    }
    if (/^#(?:[0-9a-fA-F]{3}){1,2}$/i.test(colorPropValue)) {
      let hex = colorPropValue.toUpperCase();
      if (hex.length === 4) {
        hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
      }
      return hex;
    }
    return colorPropValue.toUpperCase();
  } else {
    if (parsedRgba && parsedRgba.a !== undefined) {
      return Math.round(parsedRgba.a * 100);
    }
    return 100;
  }
}

export function hexToRgb(hex: string): RgbColor | null {
  if (!hex) return null;
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const normalizedHex = hex.replace(
    shorthandRegex,
    (m, rVal, gVal, bVal) => rVal + rVal + gVal + gVal + bVal + bVal
  );

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    normalizedHex
  );
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

export function getCurrentColorComponents(colorString: string): RgbaColor {
  if (
    !colorString ||
    colorString.toLowerCase() === "transparent" ||
    colorString.toLowerCase() === "none"
  ) {
    return { r: 0, g: 0, b: 0, a: 0 };
  }

  const parsedRgba = parseRgbaString(colorString);
  if (parsedRgba) {
    return parsedRgba;
  }

  const parsedHex = hexToRgb(colorString);
  if (parsedHex) {
    return { ...parsedHex, a: 1 };
  }

  console.warn(
    `Unrecognized color format in getCurrentColorComponents: ${colorString}. Defaulting to opaque black.`
  );
  return { r: 0, g: 0, b: 0, a: 1 };
}

export function constructColorResult(
  r: number,
  g: number,
  b: number,
  a: number
): ColorResult {
  const hexString = rgbToHex(r, g, b);

  const hslPlaceholder: HSLColor = {
    h: 0,
    s: 0,
    l: (r + g + b) / 3 / 255,
    a: a,
  };

  return {
    hex: hexString,
    rgb: { r, g, b, a },
    hsl: hslPlaceholder,
  };
}
